/**
 * Talent coordination queues (17 Sep 2026): computed live from bookings, never stored, so a booking
 * leaves a queue the moment its need is met. Same idea as the billing queues over booking_balances.
 * Staffing needs come from the one shared rule in src/lib/booking/staffing.js.
 */
import { getStaffingNeeds, shouldSurfaceForCoordinator, ROLE_LABELS } from '../booking/staffing.js'

export const QUEUES = [
  { key: 'needs_photographer', title: 'Needs a photographer', hint: 'Primary slot empty, event ahead' },
  { key: 'needs_video', title: 'Needs video or 2nd shooter', hint: 'Package or add-on calls for a slot nobody holds' },
  { key: 'declined', title: 'Declined or cancelled by talent', hint: 'Needs reassignment' },
  { key: 'unconfirmed_7d', title: 'Event in 7 days, talent not confirmed', hint: 'Assigned but never acknowledged' },
  { key: 'links_today', title: 'Upload links due today', hint: 'Event today or tomorrow, no upload link yet' }
]

const dateOnly = v => (typeof v === 'string' ? v.slice(0, 10) : '')
export const addDays = (ymd, n) => { const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
export const daysOut = (eventDate, today) => Math.round((Date.parse(dateOnly(eventDate) + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) / 86400000)

const hasUploadLink = b => !!(b.photos_upload_link || b.videos_upload_link || b.raws_upload_link || b.media_upload_url)

/** Words for the open needs on one booking: "photographer + videographer". */
export function describeNeeds(needs) {
  const parts = []
  if (needs?.photographer) parts.push(ROLE_LABELS.photographer || 'photographer')
  if (needs?.videographer) parts.push(ROLE_LABELS.videographer || 'videographer')
  if (needs?.secondPhotographer) parts.push(ROLE_LABELS.second_photographer || '2nd photographer')
  if (needs?.secondVideographer) parts.push(ROLE_LABELS.second_videographer || '2nd videographer')
  return parts.join(' + ')
}

/**
 * @param {Array<object>} rows  bookings with: id, event_date, booking_status, package_type, created_at,
 *   photographer_id, videographer_id, second_photographer_id, personalization_data, add_ons (addon rows),
 *   talent_confirmed_seen, photos_upload_link, videos_upload_link, raws_upload_link, media_upload_url, is_test
 * @param {string} today YYYY-MM-DD in the business timezone
 */
export function classifyCoordinationQueues(rows, today) {
  const byQueue = Object.fromEntries(QUEUES.map(q => [q.key, []]))
  const rowsWithQueues = []
  const weekOut = addDays(today, 7)
  const tomorrow = addDays(today, 1)
  const now = new Date(today + 'T12:00:00Z')

  for (const r of rows || []) {
    if (!r || r.is_test) continue
    const status = String(r.booking_status || '').toLowerCase()
    if (status === 'cancelled' || status === 'draft' || status === 'completed') continue
    const eventDay = dateOnly(r.event_date)
    if (!eventDay || eventDay < today) continue
    const needs = getStaffingNeeds(r)
    const surfaces = shouldSurfaceForCoordinator(r, needs, now)
    const queues = []

    if (surfaces && needs.photographer) queues.push('needs_photographer')
    if (surfaces && (needs.videographer || needs.secondPhotographer || needs.secondVideographer)) queues.push('needs_video')
    if (status === 'declined_by_talent') queues.push('declined')
    if (eventDay <= weekOut && !r.talent_confirmed_seen && !needs.photographer && r.photographer_id) queues.push('unconfirmed_7d')
    if (eventDay <= tomorrow && !hasUploadLink(r)) queues.push('links_today')

    const row = { ...r, needs, needsText: describeNeeds(needs), daysOut: daysOut(eventDay, today), queues }
    for (const q of queues) byQueue[q].push(row)
    rowsWithQueues.push(row)
  }
  for (const key of Object.keys(byQueue)) byQueue[key].sort((a, b) => a.daysOut - b.daysOut)
  return { byQueue, rowsWithQueues }
}

/** Kinds in the Changes feed, with the label the page shows and whether it is on by default. */
export const FEED_KINDS = [
  { key: 'new_booking', label: 'New booking', on: true },
  { key: 'date_changed', label: 'Date changed', on: true },
  { key: 'location_changed', label: 'Location changed', on: true },
  { key: 'package_changed', label: 'Package changed', on: true },
  { key: 'package_corrected', label: 'Package corrected', on: true },
  { key: 'date_change_recorded', label: 'Date change recorded', on: false },
  { key: 'addon_added', label: 'Add-on', on: true },
  { key: 'amendment', label: 'Amendment', on: true },
  { key: 'talent_declined_job', label: 'Talent declined', on: true },
  { key: 'talent_cancelled_booking', label: 'Talent cancelled', on: true },
  { key: 'talent_assignment', label: 'Assigned', on: false },
  { key: 'talent_unassignment', label: 'Unassigned', on: true }
]
